import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import heroImageUrl from '../assets/HomepageHero.png';
import { useLanguage } from '../i18n/LanguageContext';

const HeroSection = styled.section`
  position: relative;
  min-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 140px 20px 120px;
  color: #fff;
  overflow: hidden;
  background: #0f172a;

  @media (max-width: 992px) {
    padding: 120px 20px 100px;
  }

  @media (max-width: 768px) {
    min-height: auto;
    padding: 110px 18px 90px;
  }

  @media (max-width: 480px) {
    padding: 96px 16px 80px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: radial-gradient(circle at 18% 24%, rgba(88, 156, 237, 0.35), transparent 45%),
    radial-gradient(circle at 86% 18%, rgba(0, 168, 232, 0.28), transparent 48%),
    linear-gradient(160deg, rgba(15, 23, 42, 0.2), transparent 60%);
`;

const VideoBackground = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;
`;

const HeroVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  filter: saturate(1.05);
`;

const AudioButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 3;
  border: none;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--shadow-soft);
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(15, 23, 42, 0.9);
  }

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
    font-size: 0.9rem;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1180px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 42px;
    align-items: center;
    text-align: center;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  max-width: 720px;

  @media (max-width: 768px) {
    align-items: center;
    gap: 24px;
  }
`;

const Tagline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px;
  width: fit-content;
  font-size: 13px;
  letter-spacing: 0.38em;
  text-transform: uppercase;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Heading = styled.h1`
  margin: 0;
  font-size: clamp(2.75rem, 4.8vw, 4.15rem);
  font-weight: 800;
  line-height: 1.08;
  text-shadow: 0 25px 45px rgba(15, 23, 42, 0.55);

  @media (max-width: 768px) {
    line-height: 1.1;
  }
`;

const SubHeading = styled.p`
  margin: 0;
  max-width: 620px;
  font-size: clamp(1.05rem, 2.2vw, 1.3rem);
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.82);

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 30px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  border: ${({ primary }) => (primary ? 'none' : '1px solid rgba(255, 255, 255, 0.55)')};
  background: ${({ primary }) =>
  primary ? 'linear-gradient(135deg, rgba(12, 77, 162, 1) 0%, rgba(88, 156, 237, 1) 100%)' : 'transparent'};
  color: #fff;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ primary }) => (primary ? '0 20px 45px rgba(0, 0, 0, 0.3)' : '0 18px 35px rgba(0, 0, 0, 0.22)')};
    background: ${({ primary }) =>
  primary ? 'linear-gradient(135deg, rgba(12, 77, 162, 0.92) 0%, rgba(88, 156, 237, 0.92) 100%)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const MetricsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-top: 14px;

  @media (max-width: 600px) {
    gap: 12px;
    width: 100%;
  }

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const MetricCard = styled.div`
  padding: 16px 18px;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 480px) {
    padding: 14px 16px;
  }
`;

const MetricValue = styled.span`
  font-size: clamp(1.6rem, 2.5vw, 2rem);
  font-weight: 700;
`;

const MetricLabel = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
`;

const Hero = () => {
  const { t } = useLanguage();
  const metrics = t('home.hero.metrics');
  const videoSrc = '/video/pridetrans.mp4';
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  const enableAudio = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    video.volume = 1;
    video.play().catch(() => {});
    setIsMuted(false);
  };

  useEffect(() => {
    const handleFirstClick = () => {
      enableAudio();
    };

    window.addEventListener('click', handleFirstClick, { once: true });
    return () => window.removeEventListener('click', handleFirstClick);
  }, []);

  return (
    <HeroSection onClick={enableAudio}>
      <VideoBackground>
        <HeroVideo
          ref={videoRef}
          autoPlay
          muted
          playsInline
          loop
          preload="auto"
          poster={heroImageUrl}
          onCanPlay={(event) => {
            event.currentTarget.play().catch(() => {});
          }}
        >
          <source src={videoSrc} type="video/mp4" />
        </HeroVideo>
      </VideoBackground>
      {isMuted && (
        <AudioButton type="button" onClick={(event) => {
          event.stopPropagation();
          enableAudio();
        }}>
          Enable sound
        </AudioButton>
      )}
      <HeroOverlay />
      <HeroContent>
        <TextBlock>
          <Tagline>{t('home.hero.tagline')}</Tagline>
          <Heading>{t('home.hero.heading')}</Heading>
          <SubHeading>{t('home.hero.subheading')}</SubHeading>
          <CTAContainer>
            <CTAButton primary href="/quote">{t('home.hero.ctaPrimary')}</CTAButton>
            <CTAButton href="/contact">{t('home.hero.ctaSecondary')}</CTAButton>
          </CTAContainer>
          <MetricsStrip>
            {metrics.map((metric) => (
              <MetricCard key={metric.label}>
                <MetricValue>{metric.value}</MetricValue>
                <MetricLabel>{metric.label}</MetricLabel>
              </MetricCard>
            ))}
          </MetricsStrip>
        </TextBlock>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero;